import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
// import axios from 'axios';

// MUI
import { Button, Typography, Paper, Stack, LinearProgress } from "@mui/material";

// COMPONENTS
import Header from '../components/Header';
import Banner from '../components/Banner';
import ApplicantForm from './ApplicantForm';
import PaymentForm from './PaymentForm';
import { RefundPolicy } from './RefundPolicy';

// CONTEXT
import { useAuthContext } from '../context/AuthContext';
import useWindowDimensions from '../hooks/useWindowDimensions';

// DATA
import { createApplicationValidationSchema, paymentValidationSchema, requiresLOA } from '../data/Validation';
import { initialFormValues, initialPaymentValues } from '../data/InitialValues';
import { stateOptions as publicStateOptions } from './FormData';
import {
    VIC_AUTHORITY_OPTIONS,
    VIC_HOUR_OPTIONS,
    VIC_STATE_OPTION,
} from '../data/VictoriaRules';

// API
import { fetchPricesAPI } from '../apis/prices';
import { fetchBrandsAPI } from '../apis/brands';
import { usePurchaseMutation, useAdminPurchaseMutation } from '../apis/purchase';
import { useVictoriaEnabledQuery } from '../apis/victoria';

const getDefaultState = () => window.location.hostname.includes('bodycorporateinfo') ? 'QLD' : 'NSW';
const DEFAULT_BRAND_BY_STATE = {
    NSW: 'BCS Strata Sydney',
    QLD: 'BCS Strata Brisbane',
};
const DEFAULT_HOURS_BY_STATE = {
    NSW: 1,
    QLD: 2,
    VIC: 2,
};
const BRAND_OPTIONS_KEY_BY_STATE = {
    NSW: 'nswBrandOptions',
    QLD: 'qldBrandOptions',
    VIC: 'vicBrandOptions',
};

const getBrandOptionsForState = (state, brandOptionsByState) => {
    const optionsKey = BRAND_OPTIONS_KEY_BY_STATE[state];
    return optionsKey ? brandOptionsByState?.[optionsKey] || [] : [];
};

const getDefaultBrandForState = (state, brandOptionsByState) => {
    const brandOptions = getBrandOptionsForState(state, brandOptionsByState);
    return DEFAULT_BRAND_BY_STATE[state] || brandOptions[0]?.value || '';
};

const getDefaultHoursForState = (state) => DEFAULT_HOURS_BY_STATE[state] || 1;

const calculateSearchPrice = (selectedOption, hours, schemeType) => {
    if (!selectedOption) return undefined;

    const baseCost = schemeType === 'bugta_scheme' ? selectedOption.bugtaCost : selectedOption.cost;
    return ((baseCost + (((hours - 1) / 0.5) * selectedOption.halfHourRate)) / 100)
        .toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

const getVictoriaAuthorityValue = (authorityOptions) => {
    if (!authorityOptions?.length) return '';
    return authorityOptions.some(option => option.value === 'other') ? 'other' : authorityOptions[0].value;
};


const ApplicantManager = ({ isLoginRoute }) => {
    const navigate = useNavigate();
    const { login, logout, isAuthenticated, user } = useAuthContext();
    const [eCryptLoaded, setECryptLoaded] = useState(false);
    const { mobileVersion } = useWindowDimensions();

    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [paymentInProgress, setPaymentInProgress] = useState(false);
    const [adminError, setAdminError] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [paymentRef, setPaymentRef] = useState('');
    const [formStage, setFormStage] = useState('application');
    const [key, setKey] = useState('');

    const { data: prices } = fetchPricesAPI();
    const { data: brands } = fetchBrandsAPI();
    const { data: victoriaEnabled = false } = useVictoriaEnabledQuery({ isAuthenticated, user });
    const victoriaEnabledForEmployee = isAuthenticated && victoriaEnabled === true;

    const formPrices = useMemo(() => {
        if (!prices) return prices;

        const filteredPrices = {
            NSW: prices.NSW || [],
            QLD: prices.QLD || [],
        };

        if (victoriaEnabledForEmployee) {
            filteredPrices.VIC = VIC_AUTHORITY_OPTIONS;
        }

        return filteredPrices;
    }, [prices, victoriaEnabledForEmployee]);

    const formBrands = useMemo(() => {
        if (!brands) return brands;

        return {
            nswBrandOptions: brands.nswBrandOptions || [],
            qldBrandOptions: brands.qldBrandOptions || [],
            vicBrandOptions: victoriaEnabledForEmployee ? brands.vicBrandOptions || [] : [],
        };
    }, [brands, victoriaEnabledForEmployee]);

    const formStateOptions = useMemo(() => {
        if (!victoriaEnabledForEmployee) return publicStateOptions;
        return [...publicStateOptions, VIC_STATE_OPTION];
    }, [victoriaEnabledForEmployee]);

    const applicationValidationSchema = useMemo(
        () => createApplicationValidationSchema(formPrices, {
            isAuthenticated,
            victoriaEnabledForEmployee,
            vicBrandOptions: formBrands?.vicBrandOptions || [],
        }),
        [formPrices, formBrands, isAuthenticated, victoriaEnabledForEmployee]
    );

    // Get stored form data if isLoginRoute is true
    const getInitialFormValues = () => {
        if (isLoginRoute) {
            const storedFormData = localStorage.getItem('applicationFormData');
            if (storedFormData) {
                try {
                    const parsedData = JSON.parse(storedFormData);
                    // Check if data is older than 5 minutes (300000 milliseconds)
                    if (Date.now() - parsedData.timestamp < 300000) {
                        // Remove timestamp before returning the data
                        const { timestamp, ...formData } = parsedData;
                        return { ...initialFormValues, ...formData };
                    } else {
                        localStorage.removeItem('applicationFormData');
                        return initialFormValues;
                    }
                } catch {
                    return initialFormValues;
                }
            }
        }
        return initialFormValues;
    };

    const requiresNoMoney = (state, brand) => {
        const brandOptions = getBrandOptionsForState(state, formBrands);
        const matchedBrand = brandOptions.find(option => option.value === brand);
        return matchedBrand?.eway_group === 'X' ? true : false;
    }

    const applicationFormik = useFormik({
        initialValues: getInitialFormValues(),
        validationSchema: applicationValidationSchema,
        onSubmit: async (values) => {
            if (values.state === 'VIC' && !victoriaEnabledForEmployee) {
                setAdminError('Victoria inspection requests are only available to authenticated employees.');
                setFormStage('failed');
                return;
            }

            // If the user is authenticated and has a valid bearer token, then we hit the admin-purchase API with the values
            if (isAuthenticated) {
                const adminPurchaseData = {
                    ...values,
                    ...applicationFormik.values,
                    price: applicationFormik.values.statusToRecords,
                    files: applicationFormik.values.files.length > 0 ? applicationFormik.values.files.map(file => file.name) : [],
                    date: formatDate(dayjs(new Date())),
                }
                adminPurchaseMutation.mutate(adminPurchaseData);
                // Clear stored form data on successful admin submission
                localStorage.removeItem('applicationFormData');
            }
            else if (requiresNoMoney(applicationFormik.values.state, applicationFormik.values.brand)) {
                // No Money needed for these brands
                setPaymentInProgress(true);
                await purchaseMutation.mutate({
                    ...values,
                    price: applicationFormik.values.statusToRecords,
                    files: applicationFormik.values.files.length > 0 ? applicationFormik.values.files.map(file => file.name) : [],
                    date: formatDate(dayjs(new Date())),
                })
                // Clear stored form data on successful submission
                localStorage.removeItem('applicationFormData');
            }
            else {
                setFormStage('payment');
                // Scroll to the top of the page
                window.scrollTo(0, 0);
            }
        },
    });

    // Update validation schema when prices load
    useEffect(() => {
        if (formPrices && applicationValidationSchema) {
            applicationFormik.setFormikState((state) => ({
                ...state,
                validationSchema: applicationValidationSchema
            }));
        }
    }, [formPrices, applicationValidationSchema]);

    // Add this effect to store form data when it changes
    useEffect(() => {
        if (Object.keys(applicationFormik.values).length > 0) {
            // Add timestamp to the stored data
            const dataToStore = {
                ...applicationFormik.values,
                timestamp: Date.now()
            };
            localStorage.setItem('applicationFormData', JSON.stringify(dataToStore));
        }
    }, [applicationFormik.values]);

    const adminPurchaseMutation = useAdminPurchaseMutation({
        isAuthenticated,
        user,
        applicationFormik,
        setUploadComplete,
        setKey,
        setPaymentInProgress,
        setFormStage,
        setAdminError
    });



    useEffect(() => {
        // Remove existing script if it exists eway caches previous credentials used
        const existingScript = document.querySelector('script[src="https://secure.ewaypayments.com/scripts/eCrypt.min.js"]');
        if (existingScript) {
            existingScript.remove();
        }

        if (applicationFormik.values.state === 'VIC') {
            setECryptLoaded(false);
            return;
        }

        // console.log("VALUES", applicationFormik.values);

        // Add new script
        const script = document.createElement("script");
        script.src = "https://secure.ewaypayments.com/scripts/eCrypt.min.js";
        script.onload = () => setECryptLoaded(true);
        script.onerror = () => {
            console.error("Failed to load eCrypt script.");
            setECryptLoaded(false);
        };
        document.body.appendChild(script);
    }, [applicationFormik.values.brand, applicationFormik.values.state]);

    useEffect(() => {
        // Clear files if the current status doesn't require LOA
        if (!requiresLOA(applicationFormik.values.statusToRecords, formPrices)) {
            applicationFormik.setFieldValue('files', []);
        }
    }, [applicationFormik.values.statusToRecords, formPrices]);

    const formatDate = (date) => {
        return dayjs(date).format('YYYY-MM-DD');
    }

    const purchaseMutation = usePurchaseMutation(applicationFormik, {
        setUploadComplete,
        setKey,
        setPaymentInProgress,
        setFormStage,
        setPaymentRef,
        setErrorMessage
    });

    const paymentFormik = useFormik({
        initialValues: initialPaymentValues,
        validationSchema: paymentValidationSchema,
        onSubmit: async (values) => {
            // console.log(values);
            // alert(JSON.stringify(values, null, 2));

            // e.preventDefault();
            if (!window.eCrypt || !eCryptLoaded) {
                console.error("eCrypt library not loaded.");
                // alert("eCrypt library not loaded. Please try again later.");
                return;
            }

            try {
                const encryptedCardNumber = window.eCrypt.encryptValue(values.cardNumber.replace(/[\s-]/g, ''));
                const encryptedCVN = window.eCrypt.encryptValue(values.cvn);
                // For debugging or further processing
                const paymentData = {
                    ...values,
                    cardNumber: encryptedCardNumber,
                    cvn: encryptedCVN,
                    ...applicationFormik.values,
                    price: applicationFormik.values.statusToRecords,
                    files: applicationFormik.values.files.length > 0 ? applicationFormik.values.files.map(file => file.name) : [],
                    // date: formatDate(applicationFormik.values.date),
                    date: formatDate(dayjs(new Date())),
                };
                // console.log("TEST", paymentData);

                setPaymentInProgress(true);

                purchaseMutation.mutate(paymentData);
            } catch (error) {
                console.error("Encryption error:", error.message);
                alert("Failed to encrypt card data. Please try again.");
                setPaymentInProgress(false);
            }
        },
    });

    const searchPrice = useMemo(() => {
        if (!formPrices) return undefined;

        if (applicationFormik.values.state === 'VIC') {
            return victoriaEnabledForEmployee ? '0.00' : undefined;
        }

        if (applicationFormik.values.state === 'QLD' && !applicationFormik.values.schemeType) {
            return undefined;
        }

        const selectedOption = formPrices[applicationFormik.values.state]?.find(
            option => option.value === applicationFormik.values.statusToRecords
        );
        return calculateSearchPrice(
            selectedOption,
            applicationFormik.values.hours,
            applicationFormik.values.schemeType
        );
    }, [
        applicationFormik.values.hours,
        applicationFormik.values.schemeType,
        applicationFormik.values.state,
        applicationFormik.values.statusToRecords,
        formPrices,
        victoriaEnabledForEmployee,
    ]);

    useEffect(() => {
        if (!formPrices || !formBrands) return;

        if (applicationFormik.values.state === 'VIC' && !victoriaEnabledForEmployee) {
            const defaultState = getDefaultState();
            applicationFormik.setFieldValue('state', defaultState);
            applicationFormik.setFieldValue('brand', getDefaultBrandForState(defaultState, formBrands));
            applicationFormik.setFieldValue('dpNumber', '');
            applicationFormik.setFieldValue('hours', getDefaultHoursForState(defaultState));
            if (VIC_AUTHORITY_OPTIONS.some(option => option.value === applicationFormik.values.statusToRecords)) {
                applicationFormik.setFieldValue('statusToRecords', '');
            }
            applicationFormik.setFieldValue('files', []);
            return;
        }

        const stateBrandOptions = getBrandOptionsForState(applicationFormik.values.state, formBrands);
        const defaultBrand = getDefaultBrandForState(applicationFormik.values.state, formBrands);
        if (
            defaultBrand &&
            !stateBrandOptions.some(option => option.value === applicationFormik.values.brand)
        ) {
            applicationFormik.setFieldValue('brand', defaultBrand);
        }

        if (applicationFormik.values.state !== 'QLD' && applicationFormik.values.schemeType) {
            applicationFormik.setFieldValue('schemeType', '');
        }

        if (applicationFormik.values.state === 'VIC') {
            const vicAuthority = getVictoriaAuthorityValue(formPrices.VIC);

            if (!VIC_HOUR_OPTIONS.some(option => option.value === applicationFormik.values.hours)) {
                applicationFormik.setFieldValue('hours', 2);
            }
            if (!formPrices.VIC?.some(option => option.value === applicationFormik.values.statusToRecords)) {
                applicationFormik.setFieldValue('statusToRecords', vicAuthority);
            }
            return;
        }

        const defaultHours = getDefaultHoursForState(applicationFormik.values.state);
        if (applicationFormik.values.hours !== defaultHours) {
            applicationFormik.setFieldValue('hours', defaultHours);
        }
    }, [applicationFormik.values.state, formPrices, formBrands, victoriaEnabledForEmployee]);

    // const handleApplicationSubmit = () => {
    //     // console.log("VALUES-0", applicationFormik.values);
    //     applicationFormik.handleSubmit();
    // };

    // const handlePaymentSubmit = () => {
    //     paymentFormik.handleSubmit();
    // };

    const resetForm = () => {
        applicationFormik.resetForm();
        paymentFormik.resetForm();
        setFormStage('application');
        // Clear stored form data when form is reset
        localStorage.removeItem('applicationFormData');
    }

    const getEwayKeyFromBrand = (brand) => {
        if (applicationFormik.values.state === 'VIC') return '';

        const brandOptions = applicationFormik.values.state === 'NSW' ? formBrands.nswBrandOptions : formBrands.qldBrandOptions;
        const selectedBrand = brandOptions.find(option => option.value === brand);
        return selectedBrand?.eway_group ? `VITE_${selectedBrand.eway_group}_EWAY_CLIENT` : '';
    };

    // Add this effect to handle auto-login
    useEffect(() => {
        if (isLoginRoute && !isAuthenticated) {
            // Wrap the login call in a setTimeout to ensure MSAL is initialized
            const timer = setTimeout(() => {
                login().catch(error => {
                    console.error('Login failed:', error);
                    console.log(JSON.stringify(error, null, 2));
                    if (error.errorCode === 'user_cancelled') {
                        navigate('/');
                    }
                });
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [isLoginRoute, isAuthenticated, login]);

    if (!formBrands || !formPrices) return (
        <>
            <Header displayAll={true}/>
            <Banner displayAll={true}/>
            <LinearProgress />
        </>
    );

    return (
            <form data-eway-encrypt-key={import.meta.env[getEwayKeyFromBrand(applicationFormik.values.brand)]}>
            <Header displayAll={true}/>
            <Banner displayAll={true}/>
            <div>
                
                <Typography variant='h4' align='left' color='primary' sx={{ width: '90%', paddingTop: '20px',paddingBottom: '20px', maxWidth: '1280px', margin: '0 auto' }} gutterBottom>
                    Request and pay for scheme records access
                </Typography>
                <Typography variant='h5' align='left' color='secondary' sx={{ width: '90%', paddingBottom: '20px', maxWidth: '1280px', margin: '0 auto' }} gutterBottom>
                    We process requests during our business hours: 8.30 am to 5.00 pm, Monday to Friday.
                </Typography>
                <Typography variant='body1' align='left' color='primary' sx={{ width: '90%', paddingBottom: '10px', maxWidth: '1280px', margin: '0 auto' }} gutterBottom>
                    After submitting your request and making the payment, please allow up to seven days for QLD body corporates and ten days for NSW owners corporations before receiving access to the scheme's records.
                </Typography>
                <Typography variant='body1' align='left' color='primary' sx={{ width: '90%', paddingBottom: '40px', maxWidth: '1280px', margin: '0 auto' }} gutterBottom>
                    Please note: State laws might influence the speed of completing your records search.
                </Typography>

                <Paper sx={{ padding: '20px', elevation: 0, backgroundColor: '#EAF6F5', width: '90%', maxWidth: '1280px', margin: '0 auto' }}>
                    {(formStage === 'application' || formStage === 'payment') && (
                        <>
                            <Typography variant='h4' align='left' color='primary' gutterBottom>Inspection of scheme records request form</Typography>
                            <Typography align='left' variant='body1' color='primary' gutterBottom>To request a search of records, please complete the below form.</Typography>
                            <Typography variant='body1' color='secondary' sx={{ paddingBottom: '20px' }}>"<span style={{ color: 'red' }}>*</span>" Indicates required fields.</Typography>
                        </>
                    )}

                    {formStage === 'application' && (
                        <ApplicantForm
                            formik={applicationFormik}
                            searchPrice={searchPrice}
                            prices={formPrices}
                            brands={formBrands}
                            stateOptions={formStateOptions}
                            handleSubmit={() => applicationFormik.handleSubmit()}
                            paymentInProgress={paymentInProgress}
                        />
                    )}
                    {(formStage === 'payment' && eCryptLoaded && applicationFormik.values.state !== 'VIC') && (
                        <PaymentForm formik={paymentFormik} handleSubmit={() => paymentFormik.handleSubmit()} paymentInProgress={paymentInProgress} handleBack={() => setFormStage('application')} />
                    )}

                    {(formStage === 'success' || (formStage === 'failed' && !isAuthenticated)) && (
                        <>
                            <Typography variant='h4' align='left' color='secondary' gutterBottom>Your request has been successfully submitted</Typography>
                            {(import.meta.env.VITE_ENV === 'dev' && isAuthenticated) && <Button sx={{ marginBottom: '10px', color: 'white' }} variant='contained' color='secondary' aria-label="View Documents Now" onClick={() => navigate(`/documents?key=${key}`)}>View Documents Now - STAGING & AUTHENTICATED ONLY</Button>}
                            <Typography variant='h6' align='left' color='primary' gutterBottom>Search instructions will be emailed to {applicationFormik.values.email} when your request is processed.</Typography>
                            {paymentRef && <Typography variant='h6' align='left' color='primary' gutterBottom>Your request reference number is {paymentRef}</Typography>}
                            {/* <Button sx={{ marginBottom: '10px', color: 'white' }} variant='contained' color='secondary' aria-label="Go Back" onClick={() => resetForm()}>HOME</Button> */}
                        </>
                    )}

                    {formStage === 'ses_error' && (
                        <>
                            <Typography variant='h4' align='left' color='secondary' gutterBottom>
                                {'Unknown email error'}
                            </Typography>
                            <Typography variant='h6' align='left' color='primary' gutterBottom>
                                Please contact our customer support team at <a href='mailto:searches@picagroup.com.au'>searches@picagroup.com.au</a>
                            </Typography>
                            <Button sx={{ marginBottom: '10px', color: 'white' }} variant='contained' color='secondary' aria-label="Go Back" onClick={() => {setErrorMessage(''); setAdminError(''); setFormStage('application');}}>BACK</Button>
                        </>
                    )}

                    {(formStage === 'failed' || formStage === 'failed_auth') && adminError !== '' && (
                        <>
                            <Typography variant='h4' align='left' color='secondary' gutterBottom>{adminError}</Typography>
                            <Button sx={{ marginBottom: '10px', color: 'white' }} variant='contained' color='secondary' aria-label="Go Back" onClick={() => {setAdminError(''); setFormStage('application');}}>BACK</Button>
                        </>
                    )}

                    {formStage === 'failed_purchase' && (
                        <>
                            <Typography variant='h4' align='left' color='secondary' gutterBottom>Your payment has failed</Typography>
                            <Typography variant='h6' align='left' color='primary' gutterBottom>Please try again.</Typography>
                            <Stack direction="row" spacing={0} sx={{ width: "100%", justifyContent: "flex-end", paddingTop: '20px' }}>
                                <Button 
                                    variant="contained" 
                                    sx={{ 
                                    width: mobileVersion ? "48%" : "30%", 
                                    backgroundColor: 'white',
                                    color: "#004677",
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    }
                                    }} 
                                    onClick={() => setFormStage('payment')} 
                                    aria-label="Go Back"
                                >
                                    Back
                                </Button>
                            </Stack>
                            {/* <Button sx={{ marginBottom: '10px', color: 'white' }} variant='contained' color='secondary' aria-label="Go Back" onClick={() => setFormStage('payment')}>BACK</Button> */}
                        </>
                    )}

                    {formStage === 'failed_purchase_document' && (
                        <>
                            <Typography variant='h4' align='left' color='secondary' gutterBottom>"Sorry, an unexpected error has occurred. Please try again and contact us if the issue persists.</Typography>
                        </>
                    )}

                    {formStage === 'validation_error' && (
                        <>
                            <Typography variant='h4' align='left' color='secondary' gutterBottom>Validation Error</Typography>
                            {(errorMessage || adminError) && (
                                Array.isArray(errorMessage || adminError) ? 
                                    (errorMessage || adminError).map((error, index) => (
                                        <Typography key={index} variant='h6' align='left' color='primary' gutterBottom>
                                            {error}
                                        </Typography>
                                    )) :
                                    <Typography variant='h6' align='left' color='primary' gutterBottom>
                                        {errorMessage || adminError}
                                    </Typography>
                            )}
                            <Stack direction="row" spacing={0} sx={{ width: "100%", justifyContent: "flex-end", paddingTop: '20px' }}>
                                <Button 
                                    variant="contained" 
                                    sx={{ 
                                    width: mobileVersion ? "48%" : "30%", 
                                    backgroundColor: 'white',
                                    color: "#004677",
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    }
                                    }} 
                                    onClick={() => {
                                        setFormStage('payment');
                                        setErrorMessage('');
                                        setAdminError('');
                                    }} 
                                    aria-label="Go Back"
                                >
                                    Back
                                </Button>
                            </Stack>
                        </>
                    )}

                    {uploadProgress > 0 && (
                        <Typography variant="body2" color="textSecondary">
                            Upload Progress: {uploadProgress}%
                        </Typography>
                    )}
                </Paper>



                <RefundPolicy />

                {(formStage === 'application' || formStage === 'failed' || formStage === 'failed_auth') && (
                    <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'center', width: '90%', maxWidth: '1280px', margin: '0 auto', paddingBottom: '30px' }}>
                        {/* {isAuthenticated && <Typography variant='body1' >Welcome {user.name}</Typography>} */}
                        <Button
                            variant="contained"
                            size="small"
                            onClick={isAuthenticated ? logout : login}
                            color="white"
                            sx={{
                                color: 'primary'
                            }}
                            aria-label={isAuthenticated ? "Logout" : "Staff Login"}
                        >
                            {isAuthenticated ? "Logout" : "Staff Login"}
                        </Button>
                    </Stack>)}
            </div >
        </form>
    );
};

export default ApplicantManager;
